import { Component, OnInit, OnDestroy } from '@angular/core';
import { RouterLink, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AuthService } from '../service/auth/auth.service';
import { LoggerService } from '../service/utils/logger.service';
import { RestService } from '../service/rest/rest.service';
import { QuestionDetailModalComponent } from '../components/question-detail-modal/question-detail-modal.component';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

interface CourseOption {
  id: number;
  name: string;
}

interface SubjectOption {
  id: number;
  name: string;
  courseId: number;
}

interface UserNoteItem {
  questionId: number;
  questionHtml: string;
  subjectName: string;
  notes: string;
  notesChangedAt: string;
}

@Component({
  selector: 'app-my-notes',
  standalone: true,
  imports: [RouterLink, CommonModule, FormsModule, QuestionDetailModalComponent],
  templateUrl: './my-notes.component.html',
  styleUrl: './my-notes.component.css'
})
export class MyNotesComponent implements OnInit, OnDestroy {
  courses: CourseOption[] = [];
  subjects: SubjectOption[] = [];
  
  selectedCourseId: number | null = null; // null = "All"
  selectedSubjectId: number | null = null; // null = "All"
  
  isLoadingCourses = false;
  isLoadingSubjects = false;
  isSearching = false;
  hasSearched = false;
  
  results: UserNoteItem[] = [];
  resultDisplayHtmlById: Record<number, string> = {};
  resultNotesHtmlById: Record<number, string> = {};
  
  // Pagination
  currentPage: number = 1;
  itemsPerPage: number = 20;
  
  // Detail modal
  detailOpen: boolean = false;
  detailQuestionId: number | null = null;
  detailQuestionHtml: string | null = null;
  detailAnswersHtml: Array<string | null> = [];
  detailCorrectAnswer: string | null = null;
  
  private rawById: Record<number, UserNoteItem> = {};
  private destroy$ = new Subject<void>();
  private isInitialized = false;
  
  constructor(
    private restService: RestService,
    private authService: AuthService,
    private router: Router
  ) {
    // Reload data whenever navigation ends on /my-notes (handles same-URL navigation)
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe((event: NavigationEnd) => {
        const url: string = event.urlAfterRedirects || event.url;
        if (url.includes('/my-notes')) {
          // Only refresh if component is already initialized (prevents double load on initial navigation)
          if (this.isInitialized) {
            LoggerService.log('[MyNotes] Route activated, refreshing data');
            this.refreshData();
          }
        }
      });
  }
  
  async ngOnInit() {
    await this.loadCourses();
    // Auto-search on load
    await this.onSearch();
    this.isInitialized = true;
  }
  
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  async refreshData() {
    // Refresh search results with current filters
    await this.onSearch();
  }
  
  openDetail(questionId: number) {
    this.detailQuestionId = questionId;
    const r = this.rawById[questionId];
    if (r) {
      // Provide question HTML and answers for immediate display
      this.detailQuestionHtml = r.questionHtml || null;
      // Load full question details via search API for answers
      this.loadQuestionDetails(questionId);
    }
    this.detailOpen = true;
  }
  
  async loadQuestionDetails(questionId: number) {
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;
      
      // Use search API to get question details with answers
      const searchResults = await this.restService.searchQuestions(sessionId, {
        courseId: null,
        subjectId: null,
        question: null,
        answer: null,
        explanation: null,
        questionNumber: questionId.toString()
      });
      if (searchResults && searchResults.length > 0) {
        const q = searchResults[0];
        this.detailQuestionHtml = q.questionHtml || null;
        this.detailAnswersHtml = [q.answer1 || null, q.answer2 || null, q.answer3 || null, q.answer4 || null];
        this.detailCorrectAnswer = q.correctAnswer || null;
      }
    } catch (e) {
      LoggerService.error('Failed to load question details:', e);
    }
  }
  
  closeDetail() {
    this.detailOpen = false;
    this.detailQuestionId = null;
    this.detailQuestionHtml = null;
    this.detailAnswersHtml = [];
    this.detailCorrectAnswer = null;
  }
  
  async loadCourses() {
    this.isLoadingCourses = true;
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;
      
      const courses = await this.restService.getCourses(sessionId);
      if (!courses) {
        this.courses = [];
        return;
      }
      this.courses = courses.map(c => ({ id: Number(c.courseId), name: c.courseTitle }));
      
      // Load subjects for all courses
      await this.loadSubjects();
    } catch (e) {
      LoggerService.error('Failed to load courses:', e);
      this.courses = [];
    } finally {
      this.isLoadingCourses = false;
    }
  }
  
  async loadSubjects() {
    if (!this.selectedCourseId) {
      // Load subjects for all courses
      this.isLoadingSubjects = true;
      try {
        const user = this.authService.getUser();
        const sessionId = user?.sid;
        if (!sessionId) return;
        
        const courses = await this.restService.getCourses(sessionId);
        if (!courses) {
          this.subjects = [];
          return;
        }
        
        // Extract unique subjects from subscriptions
        const subjectMap = new Map<number, { name: string; courseId: number }>();
        for (const course of courses) {
          const courseId = Number(course.courseId);
          if (courseId) {
            const courseSubjects = await this.restService.getSubjects(sessionId, courseId);
            if (courseSubjects) {
              for (const s of courseSubjects) {
                if (s.subjectId && s.subjectTitle) {
                  subjectMap.set(s.subjectId, { name: s.subjectTitle, courseId });
                }
              }
            }
          }
        }
        
        this.subjects = Array.from(subjectMap.entries()).map(([id, data]) => ({
          id,
          name: data.name,
          courseId: data.courseId
        }));
      } catch (e) {
        LoggerService.error('Failed to load subjects:', e);
        this.subjects = [];
      } finally {
        this.isLoadingSubjects = false;
      }
    } else {
      // Load subjects for selected course
      this.isLoadingSubjects = true;
      try {
        const user = this.authService.getUser();
        const sessionId = user?.sid;
        if (!sessionId) return;
        
        const courseSubjects = await this.restService.getSubjects(sessionId, this.selectedCourseId);
        this.subjects = (courseSubjects || []).map((s: any) => ({
          id: s.subjectId,
          name: s.subjectTitle,
          courseId: this.selectedCourseId!
        }));
      } catch (e) {
        LoggerService.error('Failed to load subjects:', e);
        this.subjects = [];
      } finally {
        this.isLoadingSubjects = false;
      }
    }
    
    // Reset subject selection if it's not valid anymore
    if (this.selectedSubjectId && !this.subjects.find(s => s.id === this.selectedSubjectId)) {
      this.selectedSubjectId = null;
    }
    
    // Auto-search when filters change
    await this.onSearch();
  }
  
  async onCourseChange() {
    await this.loadSubjects();
  }
  
  async onSubjectChange() {
    await this.onSearch();
  }
  
  async onSearch() {
    if (this.isSearching) return;
    
      this.isSearching = true;
      this.hasSearched = true;
      this.results = [];
      this.resultDisplayHtmlById = {};
      this.resultNotesHtmlById = {};
      this.rawById = {};
      this.currentPage = 1; // Reset to first page on new search
    
    try {
      const user = this.authService.getUser();
      const sessionId = user?.sid;
      if (!sessionId) return;
      
      const courseId = this.selectedCourseId ? this.selectedCourseId : undefined;
      const subjectId = this.selectedSubjectId ? this.selectedSubjectId : undefined;
      
      LoggerService.log(`[MyNotes] Searching with courseId=${courseId}, subjectId=${subjectId}`);
      
      const response = await this.restService.getUserNotes(
        sessionId,
        courseId,
        subjectId
      );
      
      LoggerService.log(`[MyNotes] Received ${response?.length || 0} notes`);
      
      const rows = (response || []) as UserNoteItem[];
      this.results = rows;
      
      for (const r of rows) {
        this.rawById[r.questionId] = r;
        this.resultDisplayHtmlById[r.questionId] = this.stripHtml(r.questionHtml || '');
        this.resultNotesHtmlById[r.questionId] = this.stripHtml(r.notes || '');
      }
    } catch (e) {
      LoggerService.error('My notes search failed:', e);
      console.error('My notes search error:', e);
    } finally {
      this.isSearching = false;
    }
  }
  
  private stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
  
  abbreviateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  }
  
  getNotesDisplay(questionId: number): string {
    const item = this.rawById[questionId];
    if (!item) return '';
    const notes = this.resultNotesHtmlById[questionId] || '';
    return this.abbreviateText(notes, 150); // Max 2 lines (~150 chars)
  }
  
  getQuestionDisplay(questionId: number): string {
    const question = this.resultDisplayHtmlById[questionId] || '';
    return this.abbreviateText(question, 80); // Max 1 line (~80 chars)
  }
  
  get totalPages(): number {
    return Math.ceil(this.results.length / this.itemsPerPage);
  }
  
  get paginatedResults(): UserNoteItem[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.results.slice(start, end);
  }
  
  get pageNumbers(): number[] {
    const pages: number[] = [];
    const maxPages = Math.min(this.totalPages, 10);
    const startPage = Math.max(1, this.currentPage - 4);
    const endPage = Math.min(this.totalPages, startPage + maxPages - 1);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }
  
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  previousPage() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    }
  }
  
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.goToPage(this.currentPage + 1);
    }
  }
  
  Math = Math;
}
